import React, { useState, useRef, useEffect } from 'react';

import { worker } from '../browser';
import overrideMapping from '../api';
import { getOverrideHandlers } from '../override';

import styles from './MockConfigPanel.module.scss';

export default function MockConfigPanel() {
  const [visible, setVisible] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [overrideKey, setOverrideKey] = useState('');
  const [type, setType] = useState('success');
  const [scenario, setScenario] = useState('');
  const [delayMs, setDelayMs] = useState('');
  const [updateUrl, setUpdateUrl] = useState(true);
  const [reloadAfterApply, setReloadAfterApply] = useState(false);

  const tapCount = useRef(0);
  const lastTap = useRef(0);

  const availableKeys = Object.keys(overrideMapping);
  const scenarioOptions =
    overrideKey && overrideMapping[overrideKey]?.[type]
      ? Object.keys(overrideMapping[overrideKey][type])
      : type === 'scenarioOnly'
      ? ['httpError', 'error', 'empty']
      : [];

  const toggleDebugPanel = () => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      tapCount.current++;
    } else {
      tapCount.current = 1;
    }
    lastTap.current = now;
    if (tapCount.current >= 3) {
      setVisible((v) => !v);
      tapCount.current = 0;
    }
  };

  const touchStartHandler = () => {
    setIsTouch(true);
    toggleDebugPanel();
  };

  const clickHandler = () => {
    if (isTouch) {
      setIsTouch(false);
      return;
    }
    toggleDebugPanel();
  };

  const applyMock = () => {
    const handlers = getOverrideHandlers({ overrideKey, type, scenario, delayMs });
    worker.use(...handlers);

    console.log('[MockConfigPanel] Applied mock:', { overrideKey, type, scenario, delayMs });

    if (updateUrl) {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      params.set('override', overrideKey);
      if (type !== 'scenarioOnly') params.set('type', type);
      params.set('scenario', scenario);
      params.set('delay', delayMs);
      window.history.replaceState(null, '', `${url.pathname}?${params}`);
    }

    if (reloadAfterApply) {
      window.location.reload();
    }

    setVisible(false);
  };

  const resetMock = () => {
    worker.resetHandlers();

    const url = new URL(window.location.href);
    const params = url.searchParams;

    params.delete('override');
    if (type !== 'scenarioOnly') params.delete('type');
    params.delete('scenario');
    params.delete('delay');

    window.history.replaceState(null, '', `${url.pathname}?${params}`);

    setOverrideKey(availableKeys[0] || '');
    setType('success');
    setScenario('');
    setDelayMs('');
    setUpdateUrl(true);
    setReloadAfterApply(false);
    setVisible(false);
  };

  useEffect(() => {
    if (!overrideKey && availableKeys.length) {
      setOverrideKey(availableKeys[0]);
    }
  }, [availableKeys]);

  useEffect(() => {
    if (scenarioOptions.length && !scenarioOptions.includes(scenario)) {
      setScenario(scenarioOptions[0]);
    }
  }, [scenarioOptions]);

  return (
    <>
      <div className={styles.sensoryArea} onTouchStart={touchStartHandler} onClick={clickHandler} />

      {visible && (
        <div className={styles.mockPanel}>
          <div className={styles.mockHeader}>
            <h3 className={styles.mockTitle}>Mock Config</h3>
            <button onClick={() => setVisible(false)}>close</button>
          </div>
          <div className={styles.mockSection}>
            <div>
              <label className={styles.mockLabel}>Endpoint</label>
              <select
                className={styles.mockSelect}
                value={overrideKey}
                onChange={(e) => setOverrideKey(e.target.value)}>
                {availableKeys.map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={styles.mockLabel}>Type</label>
              <select className={styles.mockSelect} value={type} onChange={(e) => setType(e.target.value)}>
                <option value='success'>Success</option>
                <option value='error'>Error</option>
                <option value='scenarioOnly'>Scenario Only</option>
              </select>
            </div>
            <div>
              <label className={styles.mockLabel}>Scenario</label>
              <select className={styles.mockSelect} value={scenario} onChange={(e) => setScenario(e.target.value)}>
                {scenarioOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={styles.mockLabel}>Delay (ms)</label>
              <input
                type='number'
                className={styles.mockInput}
                value={delayMs}
                onChange={(e) => setDelayMs(Number(e.target.value))}
              />
            </div>
            <div>
              <label className={styles.mockLabel}>
                <input type='checkbox' checked={updateUrl} onChange={(e) => setUpdateUrl(e.target.checked)} />
                Update URL Parameters
              </label>
            </div>
            <div>
              <label className={styles.mockLabel}>
                <input
                  type='checkbox'
                  checked={reloadAfterApply}
                  onChange={(e) => setReloadAfterApply(e.target.checked)}
                />
                Reload After Apply
              </label>
            </div>
            <div className={styles.mockButtons}>
              <button className={`${styles.mockButton} ${styles.apply}`} onClick={applyMock}>
                Apply
              </button>
              <button className={`${styles.mockButton} ${styles.reset}`} onClick={resetMock}>
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
